/**
 * The recurrence rules the dialog's dropdowns can express, as RRULE bodies the
 * backend accepts (`lb.utils.recurrence.validate_recurrence_rule`). Anything
 * outside this shape is written by hand in the Advanced box.
 */

export const RECURRENCE_FREQUENCIES = [
  'daily',
  'weekdays',
  'weekly',
  'hourly',
] as const;

export type RecurrenceFrequency = (typeof RECURRENCE_FREQUENCIES)[number];

export const WEEKDAY_CODES = [
  'MO',
  'TU',
  'WE',
  'TH',
  'FR',
  'SA',
  'SU',
] as const;

export type WeekdayCode = (typeof WEEKDAY_CODES)[number];

export type RecurrenceSelection = {
  frequency: RecurrenceFrequency;
  weekdays: WeekdayCode[];
  hour: number;
  minute: number;
};

const WORKING_WEEK: WeekdayCode[] = ['MO', 'TU', 'WE', 'TH', 'FR'];

export const DEFAULT_RECURRENCE_SELECTION: RecurrenceSelection = {
  frequency: 'daily',
  weekdays: WORKING_WEEK,
  hour: 9,
  minute: 0,
};

const DAILY_RULE = /^FREQ=DAILY;BYHOUR=(\d{1,2});BYMINUTE=(\d{1,2})$/;
const WEEKLY_RULE =
  /^FREQ=WEEKLY;BYDAY=([A-Z]{2}(?:,[A-Z]{2})*);BYHOUR=(\d{1,2});BYMINUTE=(\d{1,2})$/;
const HOURLY_RULE = /^FREQ=HOURLY;BYMINUTE=(\d{1,2})$/;

const orderedWeekdays = (weekdays: WeekdayCode[]) =>
  WEEKDAY_CODES.filter((code) => weekdays.includes(code));

const isWeekdayCode = (value: string): value is WeekdayCode =>
  (WEEKDAY_CODES as readonly string[]).includes(value);

export const composeRecurrenceRule = ({
  frequency,
  weekdays = DEFAULT_RECURRENCE_SELECTION.weekdays,
  hour = DEFAULT_RECURRENCE_SELECTION.hour,
  minute = DEFAULT_RECURRENCE_SELECTION.minute,
}: {
  frequency: RecurrenceFrequency;
  weekdays?: WeekdayCode[];
  hour?: number;
  minute?: number;
}): string => {
  const time = `BYHOUR=${hour};BYMINUTE=${minute}`;
  switch (frequency) {
    case 'daily':
      return `FREQ=DAILY;${time}`;
    case 'weekdays':
      return `FREQ=WEEKLY;BYDAY=${WORKING_WEEK.join(',')};${time}`;
    case 'weekly': {
      const days = orderedWeekdays(weekdays);
      return `FREQ=WEEKLY;BYDAY=${(days.length ? days : WORKING_WEEK).join(',')};${time}`;
    }
    case 'hourly':
      return `FREQ=HOURLY;BYMINUTE=${minute}`;
  }
};

/** The dropdown selection a rule stands for, or null when only the raw box can hold it. */
export const parseRecurrenceRule = (
  rule: string
): RecurrenceSelection | null => {
  const body = rule.trim().toUpperCase();

  const hourly = HOURLY_RULE.exec(body);
  if (hourly) {
    const minute = Number(hourly[1]);
    if (minute > 59) return null;
    return { ...DEFAULT_RECURRENCE_SELECTION, frequency: 'hourly', minute };
  }

  const daily = DAILY_RULE.exec(body);
  if (daily) {
    const hour = Number(daily[1]);
    const minute = Number(daily[2]);
    if (hour > 23 || minute > 59) return null;
    return {
      ...DEFAULT_RECURRENCE_SELECTION,
      frequency: 'daily',
      hour,
      minute,
    };
  }

  const weekly = WEEKLY_RULE.exec(body);
  if (!weekly) return null;
  const hour = Number(weekly[2]);
  const minute = Number(weekly[3]);
  if (hour > 23 || minute > 59) return null;

  const days = weekly[1].split(',');
  if (!days.every(isWeekdayCode)) return null;
  const weekdays = orderedWeekdays(days);
  const isWorkingWeek =
    weekdays.length === WORKING_WEEK.length &&
    WORKING_WEEK.every((code) => weekdays.includes(code));
  return {
    frequency: isWorkingWeek ? 'weekdays' : 'weekly',
    weekdays,
    hour,
    minute,
  };
};
